import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AUTHGWCONSTANTS } from '../authorization/authconstatnts';

export interface SpinApiResponse<T> {
  code: number;
  status: boolean;
  message: string;
  result: T;
}

export interface SpinSliceInfo {
  _id: string;
  warehouseId?: string;
  campaignId?: string;
  couponId: string;
  sliceLabel: string;
  displayOrder?: number;
  weight?: number;
  maxWinners?: number;
  currentWinners?: number;
  couponCode?: string;
  couponTitle?: string;
  discount?: string;
  color?: string;
  textColor?: string;
  icon?: string;
  subLabel?: string;
  [key: string]: any;
}

export interface SpinCampaign {
  _id: any;
  warehouseId: string;
  campaignName: string;
  description?: string;
  startDate: number;
  endDate: number;
  entryLimitType: number; // 1 (Daily), 7 (Weekly), 30 (Monthly)
  maxActiveSlices: number;
  sliceInfo: SpinSliceInfo[];
  used?: boolean;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface SubmitSpinOutcomePayload {
  warehouseId: string;
  campaignId: string;
  couponId?: string;
  sliceId?: string;
}

export interface UserEarnedCoupon {
  _id: string;
  warehouseId: string;
  campaignId: string;
  couponId: string;
  sliceId: string;
  couponCode?: string;
  couponTitle?: string;
  couponDetails?: any;
  wonAt?: string | number;
  isUsed?: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class SpinWheelService {
  constructor(private http: HttpClient) {}

  private getHeaders(): any {
    try {
      const xenAuth = localStorage.getItem(AUTHGWCONSTANTS.xenAuth);
      const guestAuth = localStorage.getItem(AUTHGWCONSTANTS.guestAuth);
      const headers: any = {
        'XEN-AUTHORIZATION': xenAuth ? xenAuth : (guestAuth ? guestAuth : ''),
        ...environment.platformInfo
      };
      return headers;
    } catch {
      return { ...environment.platformInfo };
    }
  }

  /**
   * Retrieve active campaigns and associated slices for the user to spin.
   * URL: /api/user/spin/history
   * Method: GET
   */
  getActiveCampaigns(warehouseId?: string): Observable<SpinApiResponse<SpinCampaign[]>> {
    let params: any = {};
    if (warehouseId) {
      params.warehouseId = warehouseId;
    }
    return this.http.get<SpinApiResponse<SpinCampaign[]>>(
      `${environment.proxyApiUrl}/api/user/spin/history`,
      {
        headers: this.getHeaders(),
        params
      }
    );
  }

  /**
   * Log a user's spin outcome, grant corresponding coupon, and update usage limits.
   * URL: /api/user/spin/history
   * Method: POST
   */
  submitSpinOutcome(payload: SubmitSpinOutcomePayload): Observable<SpinApiResponse<any>> {
    return this.http.post<SpinApiResponse<any>>(
      `${environment.proxyApiUrl}/api/user/spin/history`,
      payload,
      {
        headers: this.getHeaders()
      }
    );
  }

  /**
   * Retrieve the history of unavailed coupons won by the user via the spin wheel.
   * URL: /api/user/spin/coupons
   * Method: GET
   */
  getUserEarnedCoupons(warehouseId: string): Observable<SpinApiResponse<UserEarnedCoupon[]>> {
    return this.http.get<SpinApiResponse<UserEarnedCoupon[]>>(
      `${environment.proxyApiUrl}/api/user/spin/coupons`,
      {
        headers: this.getHeaders(),
        params: { warehouseId }
      }
    );
  }
}
